import React, { useMemo, useEffect, useRef } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid, LabelList
} from "recharts";
import ChartSummaryRows from "./ChartSummaryRows";


const COLOR_DEFAULT  = "#f6615f";
const COLOR_TRAINING = "#ff9a98";
const COLOR_READING  = "#4a90d9";

const makeTooltip = ({ rows, usePayloadValues = false }) => {
  const Square = ({ color }) => (
    <span
      style={{
        display: "inline-block",
        width: 12,
        height: 12,
        background: color,
        marginRight: 8,
        border: "1px solid #ccc",
      }}
    />
  );

  return ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const rowsToRender = usePayloadValues
      ? payload
          .filter((p) => p && p.dataKey)
          .map((p) => ({ label: p.name, value: p.value, color: p.fill }))
      : rows;

    if (!rowsToRender?.length) return null;
    return (
      <div
        style={{
          background: "#fff",
          border: "1px solid #999",
          borderRadius: 4,
          padding: 10,
          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
          fontSize: 13,
          color: "#222",
          minWidth: 170,
        }}
      >
        {rowsToRender.map((row, idx) => (
          <div
            key={row.label}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: idx === rows.length - 1 ? 0 : 4,
            }}
          >
            <Square color={row.color} />
            <span>{row.label}: {row.value}</span>
          </div>
        ))}
      </div>
    );
  };
};

// Pure helper — not a hook, safe to define outside component
const buildCounts = (records) => {
  const map = {};
  records.forEach((rec) => {
    const acNo = String(rec.action_card_number ?? "").trim();
    if (!acNo) return;
    map[acNo] = (map[acNo] ?? 0) + 1;
  });
  return map;
};

const RiskBarChart = ({
  defaultRecords  = [],
  trainingRecords = [],
  readRecords     = [],
  showTraining    = false,
  showRead        = false,
  actionCards     = [],
  lang            = "en",
  height = 600,
  onDataUpdate,
}) => {
  // ── Every hook called unconditionally at the top ──────────────────────────
  const cbRef = useRef(onDataUpdate);

  useEffect(() => {
    cbRef.current = onDataUpdate;
  }, [onDataUpdate]);

  const uniqueActionCards = useMemo(
    () => {
      const seen = new Set();
      return actionCards.filter((card) => {
        const key = String(card?.ac_no ?? "");
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },
    [actionCards]
  );

  const defaultCounts = useMemo(
    () => buildCounts(defaultRecords),
    [defaultRecords]
  );

  const trainingCounts = useMemo(
    () => buildCounts(trainingRecords),
    [trainingRecords]
  );

  const readCounts = useMemo(
    () => buildCounts(readRecords),
    [readRecords]
  );

  const chartData = useMemo(
    () =>
      uniqueActionCards.map((card) => {
        const acNo  = String(card.ac_no);
        const label = lang === "dk" ? card.danish_short_name : card.english_short_name;
        return {
          category:     label,
          ac_no:        acNo,
          english_name: card.english_name,
          danish_name:  card.danish_name,
          default:      defaultCounts[acNo]  ?? 0,
          training:     trainingCounts[acNo] ?? 0,
          reading:      readCounts[acNo]     ?? 0,
        };
      }),
    [uniqueActionCards, defaultCounts, trainingCounts, readCounts, lang]
  );

  useEffect(() => {
    cbRef.current?.(chartData);
  }, [chartData]);
  // ─────────────────────────────────────────────────────────────────────────

  const defaultLabel  = lang === "dk" ? "Faktisk hændelse" : "Real incident";
  const trainingLabel = lang === "dk" ? "Træning" : "Training";
  const readingLabel  = lang === "dk" ? "Læsning" : "Reading";
  const legendDefault = lang === "dk" ? "Faktisk hændelse" : "Real incident";
  const legendTraining = lang === "dk" ? "Træning" : "Training";
  const legendReading = lang === "dk" ? "Læst" : "Read";

  // Build bars array without any conditional hook calls
  const bars = [
    <Bar key="default" dataKey="default" fill={COLOR_DEFAULT} name={defaultLabel}>
      <LabelList dataKey="default" position="top" formatter={(v) => (v ? v : "")} style={{ fontSize: 11, fill: "#111" }} />
    </Bar>,
    showTraining
      ? (
        <Bar key="training" dataKey="training" fill={COLOR_TRAINING} name={trainingLabel}>
          <LabelList dataKey="training" position="top" formatter={(v) => (v ? v : "")} style={{ fontSize: 11, fill: "#333" }} />
        </Bar>
      )
      : null,
    showRead
      ? (
        <Bar key="reading" dataKey="reading" fill={COLOR_READING} name={readingLabel}>
          <LabelList dataKey="reading" position="top" formatter={(v) => (v ? v : "")} style={{ fontSize: 11, fill: "#333" }} />
        </Bar>
      )
      : null,
  ];

  const totalIncidents = defaultRecords.length;
  const totalTraining = trainingRecords.length;
  const totalReading = readRecords.length;

  const summaryRows = useMemo(() => {
    const rows = [
      {
        label: legendDefault,
        value: totalIncidents,
        color: COLOR_DEFAULT,
        show: true,
      },
    ];
    if (showTraining) {
      rows.push({ label: legendTraining, value: totalTraining, color: COLOR_TRAINING, show: true });
    }
    if (showRead) {
      rows.push({ label: legendReading, value: totalReading, color: COLOR_READING, show: true });
    }
    return rows;
  }, [legendDefault, legendTraining, legendReading, totalIncidents, totalTraining, totalReading, showTraining, showRead]);

  const tooltipContent = useMemo(
    () =>
      makeTooltip({ rows: summaryRows, usePayloadValues: true }),
    [summaryRows]
  );

  return (
    <div style={{ width: "100%", height }}>
      <ChartSummaryRows rows={summaryRows} />

      <ResponsiveContainer width="100%" height="90%">
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
          <XAxis
            dataKey="category"
            interval={0}
            angle={-90}
            textAnchor="end"
            height={130}
            tick={{ fontSize: 11 }}
          />
          <YAxis allowDecimals={false} />
          <Tooltip content={tooltipContent} />
          <Legend />
          {bars.filter(Boolean)}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default RiskBarChart;
